import * as React from 'react'
import { v4 as uuid } from 'uuid';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';

const SideContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 244px;
  margin-top: 64px;
  margin-left: 28px;
  color: #1a1a1a;
  position: sticky;
  top: 32px;
  height: fit-content;
  min-width: 200px;

  @media screen and (max-width: 1023px) {
    display: none;
  }
`

const SideTableContents = styled.div`
  height: 100%;
  width: 100%;
  margin: 8px 0;
  font-size: 13px;
  max-height: calc(100vh - 96px);
  overflow-x: hidden;
  overflow-y: auto;
`

const SideTableSection = styled.section<{ isActive: boolean; level: number }>`
  padding-top: 6px;
  padding-bottom: 6px;
  padding-left: ${({ level }) => 16 + (level - 1) * 8}px;
  padding-right: 16px;
  border-color: #f2f2f2;
  border-style: solid;
  border-inline-start-width: 2px;
  border-inline-end-width: 0px;
  border-top: none;
  border-bottom: none;
  color: #737373;

  ${({ isActive }) => isActive && css`
    color: #1a1a1a;
    border-color: #737373;
  `}

  a {
    color: currentColor;
    text-decoration: none;
  }
`

interface Item {
  id: string;
  text: string;
  level: number;
  element: HTMLElement;
}

export const generateTableOfContents = (contentElement: HTMLElement): Item[] => {
  if (!contentElement) return [];

  // Find all heading elements (h1-h6)
  const headings = contentElement.querySelectorAll('h1, h2, h3, h4, h5, h6');

  return Array.from(headings).map((heading: HTMLElement) => {
    // Get heading level from tag name (h1 -> 1, h2 -> 2, etc.)
    const level = parseInt(heading.tagName.substring(1), 10);

    // Generate an ID if none exists
    if (!heading.id) heading.id = `h_${uuid()}`
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');

    return {
      id: heading.id,
      text: heading.textContent,
      level: level,
      element: heading
    };
  });
};

const InlineTableContent = styled.div`
  display: none;
  flex-direction: column;
  padding: 0;
  border: 1px solid rgb(230, 230, 230);
  border-top: none;
  border-radius: 0 0 10px 10px;
  background: white;
`

const InlineTableHeaderIcon = styled.div`
  display: flex;
  width: fit-content;
  height: fit-content;
  transition-property: transform;
  transition-timing-function: cubic-bezier(.4, 0, .2, 1);
  transition-duration: .15s;

  svg {
    width: 16px;
    height: 16px;
  }
`

const InlineContainer = styled.div<{ open: boolean }>`
  display: none;
  flex-direction: column;
  width: 100%;
  overflow-y: auto;
  margin-bottom: 28px;
  box-shadow: 0 0 #0000, 0 0 #0000, 0px 2px 8px rgba(0,0,0,0.06);

  @media screen and (max-width: 1023px) {
    display: flex;
  }

  ${props => props.open && css`
    ${InlineTableContent} {
      display: flex;
    }

    ${InlineTableHeader} {
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }

    ${InlineTableHeaderIcon} {
      transform: rotate(180deg);
    }
  `}
`

const InlineTableHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  font-size: 16px;
  border: 1px solid rgb(230, 230, 230);
  color: #1a1a1a;
  border-radius: 10px;
  height: 40px;
  min-height: 40px;
  padding: 0 16px;
  cursor: pointer;
  width: 100%;
  background: white;
`

const InlineTableSection = styled.section<{ isActive: boolean; level: number }>`
  padding-top: 6px;
  padding-bottom: 6px;
  padding-left: ${({ level }) => 16 + (level - 1) * 8}px;
  padding-right: 16px;
  color: #737373;
  transition: color 0.2s ease-in-out;

  ${({ isActive }) => isActive && css`
    color: #1a1a1a;
    border-color: #737373;
  `}

  &:hover {
    color: #1a1a1a;
  }

  a {
    color: currentColor;
    text-decoration: none !important;
  }
`

interface DynamicTableOfContentsProps {
  contentRef: React.RefObject<HTMLElement>;
  inline?: boolean
}

const TableOfContent: React.FC<DynamicTableOfContentsProps> = ({ contentRef, inline }) => {
  const { t } = useTranslation()
  const [items, setItems] = React.useState([]);
  const [activeId, setActiveId] = React.useState('');
  const [inlineMenuOpen, setInlineMenuOpen] = React.useState(false);

  React.useEffect(() => {
    if (!contentRef.current) return;

    // Generate TOC items from content
    const items = generateTableOfContents(contentRef.current);
    setItems(items);
    setActiveId(items[0]?.id || '');

    // Setup intersection observer to detect active section
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      {
        rootMargin: '40px 0px -95% 0px',
        threshold: 0
      }
    );

    items.forEach((item) => {
      if (item.element) {
        observer.observe(item.element);
      }
    });

    return () => {
      items.forEach((item) => {
        if (item.element) {
          observer.unobserve(item.element);
        }
      });
    };
  }, [contentRef.current]);

  if (items.length === 0) return null;

  const onToggleInlineMenu = () => {
    setInlineMenuOpen(!inlineMenuOpen);
  }

  const onItemClick = (id: string) => {
    const element = document.getElementById(id);
    if (element) element.scrollIntoView({ behavior: 'instant', block: 'start', inline: 'nearest' });
    setActiveId(id);
  }

  if (inline) {
    return (
      <InlineContainer open={inlineMenuOpen}>
        <InlineTableHeader onClick={onToggleInlineMenu}>
          {t('TableOfContent::Table of contents')}

          <InlineTableHeaderIcon>
            <svg transform="" width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M3.93353 5.93451C4.24595 5.62209 4.75248 5.62209 5.0649 5.93451L7.99922 8.86882L10.9335 5.93451C11.246 5.62209 11.7525 5.62209 12.0649 5.93451C12.3773 6.24693 12.3773 6.75346 12.0649 7.06588L8.5649 10.5659C8.25249 10.8783 7.74595 10.8783 7.43353 10.5659L3.93353 7.06588C3.62111 6.75346 3.62111 6.24693 3.93353 5.93451Z" fill="currentColor"></path></svg>
          </InlineTableHeaderIcon>
        </InlineTableHeader>

        <InlineTableContent>
          {items.map((item) => (
            <InlineTableSection key={item.id} level={item.level} isActive={activeId === item.id}>
              <a href='javascript://' onClick={() => onItemClick(item.id)}>{item.text}</a>
            </InlineTableSection>
          ))}
        </InlineTableContent>

      </InlineContainer>
    )
  }

  return (
    <SideContainer>
      <SideTableContents>
        {items.map((item) => (
          <SideTableSection key={item.id} level={item.level} isActive={activeId === item.id}>
            <a href='javascript://' onClick={() => onItemClick(item.id)}>{item.text}</a>
          </SideTableSection>
        ))}
      </SideTableContents>
    </SideContainer>
  );
};

export default TableOfContent